import {
  Banners_BannerClosedRequestModel,
  Banners_FeatureBannerType,
  Banners_TargetBannerLocation,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestModels_Banners_FeatureBannerInteractionRequestModel,
  ResponseModels_Banners_CallToActionBannerResponseModel,
  ResponseModels_Banners_FeatureBannerStateResponseModel,
  ResponseModels_Banners_TargetBannerResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetCallToActionBanner = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
};
/**
 * Get banner to show customer
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The customerId.</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns ResponseModels_Banners_CallToActionBannerResponseModel Success
 */
export function getCallToActionBanner(
  { businessUnit, label, customerId }: GetCallToActionBanner,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Banners_CallToActionBannerResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/banners/banner`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type BannerClosedAction = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: Banners_BannerClosedRequestModel;
};
/**
 * Set banner closed by customer
<param name="businessUnit">The businessUnit.</param><param name="label">The label.</param><param name="customerId">The onboard request model.</param><param name="requestModel">The BannerClosed request model.</param><param name="cancellationToken">The cancellation token.</param>
 * 
 * @returns void 
 */
export function bannerClosedAction(
  { businessUnit, label, customerId, requestBody }: BannerClosedAction,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/banners/banner/closed`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetFeatureBannerState = {
  label: DC_Repositories_Base_Enumerations_Label;
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  customerId: number;
  bannerType: Banners_FeatureBannerType;
};
/**
 * Get a user's banner progress
 *
 * @returns ResponseModels_Banners_FeatureBannerStateResponseModel Success
 */
export function getFeatureBannerState(
  { label, businessUnit, customerId, bannerType }: GetFeatureBannerState,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Banners_FeatureBannerStateResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/feature-banner/${bannerType}/state`,
      errors: { 400: 'Bad Request', 500: 'Server Error' },
    },
    requestConfig,
  );
}

type TrackFeatureBannerInteraction = {
  label: DC_Repositories_Base_Enumerations_Label;
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  customerId: number;
  bannerType: Banners_FeatureBannerType;
  requestBody: RequestModels_Banners_FeatureBannerInteractionRequestModel;
};
/**
 * Call to track interactions with the given banner. Used for progressing in the banner adventure or terminating it
 *
 * @returns void
 */
export function trackFeatureBannerInteraction(
  { label, businessUnit, customerId, bannerType, requestBody }: TrackFeatureBannerInteraction,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/feature-banner/${bannerType}/interactions`,
      body: requestBody,
      errors: { 400: 'Bad Request', 500: 'Server Error' },
    },
    requestConfig,
  );
}

type GetTargetBanner = {
  label: DC_Repositories_Base_Enumerations_Label;
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  customerId: number;
  location?: Banners_TargetBannerLocation;
};
/**
 * Get Target banner of a specific customer
 *
 * @returns ResponseModels_Banners_TargetBannerResponseModel Success
 */
export function getTargetBanner(
  { label, businessUnit, customerId, location }: GetTargetBanner,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Banners_TargetBannerResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/banner/target`,
      query: { location },
      errors: { 400: 'Bad Request', 500: 'Server Error' },
    },
    requestConfig,
  );
}
